import { useCallback, useEffect, useMemo, useState, type CSSProperties, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { PanelLoading } from "../../../components/Loading";
import { useToast } from "../../../components/Toast";
import { Select } from "../../../components/Select";
import { localizedName } from "../../../lib/localizedName";
import { useAuth } from "../../../auth/AuthContext";
import { SUPPORTED_LOCALES } from "../../../i18n";
import type { ApiError } from "../../../api/client";
import {
  CONTROL_POINT_KINDS,
  controlPointApi,
  type ControlPoint,
  type ControlPointKind,
} from "../../../api/controlPoints";
import { qualityApi, productionStepApi, type ProductionStep, type QualityParameter } from "../../../api/quality";

/**
 * Kontrolne tačke: where a sample is taken, and what is measured there.
 *
 * In the lab program a sampling point was a name and a list of tests, and everything else about it lived
 * inside the name — „PRIPREMA- FLEKIČAR  G214" was the only way to learn that the point is the flaker in
 * the preparation plant. Here the plant, the machine and the kind of material are fields, which is what
 * makes a report by production step possible at all.
 *
 * That decomposition is our reading of their names, and it is editable rather than fixed, because the head
 * of quality is the one who configures tests — so she configures the places too, on her own screen. The
 * original name stays beside ours: the people who read this worked that program for ten years and
 * recognise the old name first, typos included.
 *
 * A point is never deleted, only retired. Up to ten thousand archived analyses point at the busiest one,
 * and removing it would strip the place out of ten years of findings.
 */
export default function QualityControlPointsPage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const lang = i18n.resolvedLanguage ?? "sr";
  const mayManage = hasPermission("control_points:manage");
  const canDelete = hasPermission("section:control_points:full");

  const [points, setPoints] = useState<ControlPoint[] | null>(null);
  const [steps, setSteps] = useState<ProductionStep[]>([]);
  const [parameters, setParameters] = useState<QualityParameter[]>([]);
  const [editing, setEditing] = useState<ControlPoint | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      setPoints(await controlPointApi.list(false));
    } catch (e) {
      toast.error((e as ApiError).message);
      setPoints([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void load();
    void productionStepApi
      .list(false)
      .then(setSteps)
      .catch(() => setSteps([]));
    void qualityApi
      .listParameters(true)
      .then(setParameters)
      .catch(() => setParameters([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function kindLabel(kind: ControlPointKind): string {
    switch (kind) {
      case "RAW":
        return t("lab.kind.RAW", "Sirovina");
      case "SEMI_PRODUCT":
        return t("lab.kind.SEMI_PRODUCT", "Poluproizvod");
      case "PRODUCT":
        return t("lab.kind.PRODUCT", "Proizvod");
      default:
        return t("lab.kind.UTILITY", "Voda i pomoćno");
    }
  }

  async function retire(p: ControlPoint) {
    if (!window.confirm(t("controlPoints.confirmRetire", "Povući tačku {{c}} iz upotrebe?", { c: p.code })))
      return;
    try {
      await controlPointApi.retire(p.id);
      toast.success(t("controlPoints.retired", "Tačka je povučena iz upotrebe."));
      await load();
    } catch (e) {
      toast.error((e as ApiError).message);
    }
  }

  const grouped = useMemo(() => {
    if (!points) return [];
    return CONTROL_POINT_KINDS.map((k) => ({
      kind: k,
      items: points.filter((p) => p.kind === k),
    })).filter((g) => g.items.length > 0);
  }, [points]);

  const withoutStep = (points ?? []).filter((p) => !p.productionStepId).length;

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <h1>{t("controlPoints.title", "Kontrolne tačke")}</h1>
          <div className="sub">
            {t(
              "controlPoints.lead",
              "Mesta na kojima se uzima uzorak, preuzeta iz laboratorijskog programa. Tamo je tačka bila ime i spisak testova; ovde su pogon, mašina i vrsta materijala polja, pa izveštaj po koraku proizvodnje uopšte postaje moguć.",
            )}
          </div>
        </div>
        {mayManage && (
          <button
            type="button"
            className="btn primary"
            onClick={() => {
              setEditing(null);
              setCreating(true);
            }}
          >
            {t("controlPoints.new", "Nova tačka")}
          </button>
        )}
      </div>

      {points === null ? (
        <PanelLoading text={t("common.loading")} />
      ) : (
        <>
          {mayManage && (creating || editing) && (
            <ControlPointForm
              point={editing}
              steps={steps}
              parameters={parameters}
              onClose={() => {
                setCreating(false);
                setEditing(null);
              }}
              onSaved={async () => {
                setCreating(false);
                setEditing(null);
                await load();
              }}
            />
          )}

          {points.length === 0 ? (
            <div className="empty">{t("controlPoints.none", "Nema unetih kontrolnih tačaka.")}</div>
          ) : (
            <>
              {withoutStep > 0 && (
                <div className="card">
                  <div className="card-b">
                    <p className="muted" style={{ margin: 0 }}>
                      {t(
                        "controlPoints.withoutStep",
                        "{{n}} tačaka nije smešteno na korak proizvodnje. Korak je upisan samo tamo gde ga nosi naziv iz programa ili šifra opreme; ostalo se ne pogađa nego se dopunjuje ovde.",
                        { n: withoutStep },
                      )}
                    </p>
                  </div>
                </div>
              )}

              {grouped.map((g) => (
                <div className="card" key={g.kind}>
                  <div className="card-h" style={{ flexWrap: "wrap", gap: 8 }}>
                    <strong>{kindLabel(g.kind)}</strong>
                    <span className="muted">
                      {t("controlPoints.count", "{{n}} tačaka", { n: g.items.length })}
                    </span>
                  </div>
                  <div className="card-b">
                    <table className="tbl">
                      <thead>
                        <tr>
                          <th>{t("controlPoints.col.name", "Tačka")}</th>
                          <th>{t("controlPoints.col.step", "Korak proizvodnje")}</th>
                          <th>{t("controlPoints.col.equipment", "Oprema")}</th>
                          <th>{t("controlPoints.col.parameters", "Šta se meri")}</th>
                          <th style={{ textAlign: "right" }}>
                            {t("controlPoints.col.analyses", "Analiza")}
                          </th>
                          {mayManage && <th aria-label={t("common.actions", "Akcije")} />}
                        </tr>
                      </thead>
                      <tbody>
                        {g.items.map((p) => (
                          <tr key={p.id} style={p.active ? undefined : { opacity: 0.6 }}>
                            <td>
                              <div>
                                {localizedName({ name: p.code, nameI18n: p.nameI18n }, lang)}
                                {!p.active && (
                                  <>
                                    {" "}
                                    <span className="tag neutral">
                                      {t("controlPoints.inactive", "povučena")}
                                    </span>
                                  </>
                                )}
                              </div>
                              {p.legacyName && (
                                <div className="muted" style={{ fontSize: 12 }}>
                                  {p.legacyName}
                                </div>
                              )}
                              {p.note && (
                                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                                  {p.note}
                                </div>
                              )}
                            </td>
                            <td>
                              {p.productionStepId ? (
                                localizedName(
                                  { name: p.productionStepCode ?? "", nameI18n: p.productionStepName },
                                  lang,
                                )
                              ) : (
                                <span className="muted">—</span>
                              )}
                            </td>
                            <td>
                              {p.equipmentCode ? (
                                <span style={{ fontFamily: "var(--mono)" }}>{p.equipmentCode}</span>
                              ) : (
                                <span className="muted">—</span>
                              )}
                            </td>
                            <td>
                              {p.parameters.length === 0 ? (
                                <span className="muted">
                                  {t("controlPoints.noParameters", "nije određeno")}
                                </span>
                              ) : (
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                  {p.parameters.map((q) => (
                                    <span key={q.parameterId} className="tag neutral">
                                      {localizedName({ name: q.key, nameI18n: q.nameI18n }, lang)}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                              {p.analyses.toLocaleString(lang)}
                            </td>
                            {mayManage && (
                              <td style={{ whiteSpace: "nowrap" }}>
                                <button
                                  type="button"
                                  className="btn sm"
                                  onClick={() => {
                                    setCreating(false);
                                    setEditing(p);
                                  }}
                                >
                                  {t("common.edit", "Izmeni")}
                                </button>
                                {p.active && (
                                  <button
                                    type="button"
                                    className="btn sm"
                                    style={{ marginLeft: 6 }}
                                    disabled={!canDelete} onClick={() => void retire(p)}
                                  >
                                    {t("controlPoints.retire", "Povuci")}
                                  </button>
                                )}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
}

/**
 * The editor. One form for creating and for correcting, because it is the same decision either way:
 * what this place is, where it sits, and what is measured on it.
 *
 * The code is editable only while creating. Archived analyses and prescribed plan items point at it, so
 * changing it later would orphan them — the field says so rather than silently refusing.
 */
function ControlPointForm({
  point,
  steps,
  parameters,
  onClose,
  onSaved,
}: {
  point: ControlPoint | null;
  steps: ProductionStep[];
  parameters: QualityParameter[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const lang = i18n.resolvedLanguage ?? "sr";

  const [code, setCode] = useState(point?.code ?? "");
  const [names, setNames] = useState<Record<string, string>>(point?.nameI18n ?? {});
  const [kind, setKind] = useState<ControlPointKind>(point?.kind ?? "PRODUCT");
  const [stepId, setStepId] = useState(point?.productionStepId ?? "");
  const [equipment, setEquipment] = useState(point?.equipmentCode ?? "");
  const [note, setNote] = useState(point?.note ?? "");
  const [sortOrder, setSortOrder] = useState(String(point?.sortOrder ?? 0));
  const [active, setActive] = useState(point?.active ?? true);
  const [chosen, setChosen] = useState<string[]>((point?.parameters ?? []).map((p) => p.parameterId));
  const [busy, setBusy] = useState(false);

  /** Grouped by discipline: microbiology comes back days after chemistry and is chosen knowing that. */
  const groups = useMemo(() => {
    const by = new Map<string, QualityParameter[]>();
    for (const p of parameters) {
      const k = p.discipline ?? "CHEMISTRY";
      if (!by.has(k)) by.set(k, []);
      by.get(k)!.push(p);
    }
    return ["CHEMISTRY", "GRAIN", "MICROBIOLOGY"]
      .filter((k) => by.has(k))
      .map((k) => ({ discipline: k, items: by.get(k)! }));
  }, [parameters]);

  function disciplineLabel(d: string): string {
    if (d === "MICROBIOLOGY") return t("lab.discipline.MICROBIOLOGY", "Mikrobiologija");
    if (d === "GRAIN") return t("lab.discipline.GRAIN", "Sojino zrno");
    return t("lab.discipline.CHEMISTRY", "Hemija");
  }

  function toggle(id: string) {
    setChosen((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        nameI18n: names,
        kind,
        productionStepId: stepId || undefined,
        clearProductionStep: stepId ? undefined : true,
        equipmentCode: equipment.trim(),
        note: note.trim(),
        sortOrder: Number(sortOrder),
      };
      if (point) {
        await controlPointApi.update(point.id, { ...body, active });
        await controlPointApi.setParameters(point.id, chosen);
      } else {
        const created = await controlPointApi.create({
          ...body,
          code: code.trim().toUpperCase(),
          parameterIds: chosen,
        });
        if (!created) throw new Error("");
      }
      toast.success(t("controlPoints.saved", "Kontrolna tačka je sačuvana."));
      await onSaved();
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  const polje: CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };
  const oznaka: CSSProperties = { fontSize: 12, color: "var(--ink-3)" };
  const mreza: CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
  };

  return (
    <div className="card">
      <div className="card-h">
        <strong>
          {point
            ? t("controlPoints.editTitle", "Uređivanje kontrolne tačke")
            : t("controlPoints.new", "Nova tačka")}
        </strong>
      </div>
      <div className="card-b">
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={mreza}>
            <label style={polje}>
              <span style={oznaka}>{t("controlPoints.form.code", "Šifra")}</span>
              <input
                className="input"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                disabled={!!point}
                required
                pattern="[A-Z][A-Z0-9_-]*"
              />
              {point && (
                <span style={{ ...oznaka, fontSize: 11 }}>
                  {t(
                    "controlPoints.form.codeImmutable",
                    "Šifra se ne menja — arhivirane analize pokazuju na nju.",
                  )}
                </span>
              )}
            </label>
            <label style={polje}>
              <span style={oznaka}>{t("controlPoints.form.kind", "Vrsta materijala")}</span>
              <Select
                value={kind}
                options={CONTROL_POINT_KINDS.map((k) => ({
                  value: k,
                  label:
                    k === "RAW"
                      ? t("lab.kind.RAW", "Sirovina")
                      : k === "SEMI_PRODUCT"
                        ? t("lab.kind.SEMI_PRODUCT", "Poluproizvod")
                        : k === "PRODUCT"
                          ? t("lab.kind.PRODUCT", "Proizvod")
                          : t("lab.kind.UTILITY", "Voda i pomoćno"),
                }))}
                onChange={(v) => setKind(v as ControlPointKind)}
                ariaLabel={t("controlPoints.form.kind", "Vrsta materijala")}
              />
            </label>
            <label style={polje}>
              <span style={oznaka}>{t("controlPoints.col.step", "Korak proizvodnje")}</span>
              <Select
                value={stepId}
                options={[
                  { value: "", label: t("controlPoints.form.noStep", "bez koraka") },
                  ...steps.map((s) => ({
                    value: s.id,
                    label: localizedName({ name: s.code, nameI18n: s.nameI18n }, lang),
                  })),
                ]}
                onChange={setStepId}
                ariaLabel={t("controlPoints.col.step", "Korak proizvodnje")}
              />
            </label>
            <label style={polje}>
              <span style={oznaka}>{t("controlPoints.col.equipment", "Oprema")}</span>
              <input
                className="input"
                value={equipment}
                onChange={(e) => setEquipment(e.target.value)}
                placeholder="G214"
              />
              <span style={{ ...oznaka, fontSize: 11 }}>
                {t("controlPoints.form.equipmentHint", "Šifra pozicije iz registra opreme, ako je tačka jedna mašina.")}
              </span>
            </label>
            <label style={polje}>
              <span style={oznaka}>{t("controlPoints.form.sortOrder", "Redosled")}</span>
              <input
                className="input"
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              />
            </label>
            {point && (
              <label style={{ ...polje, justifyContent: "flex-end" }}>
                <span style={oznaka}>{t("controlPoints.form.active", "U upotrebi")}</span>
                <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
              </label>
            )}
          </div>

          <label style={polje}>
            <span style={oznaka}>{t("controlPoints.form.note", "Napomena")}</span>
            <input className="input" value={note} onChange={(e) => setNote(e.target.value)} />
          </label>

          <div style={polje}>
            <span style={oznaka}>{t("controlPoints.form.names", "Naziv, po jeziku")}</span>
            <div style={mreza}>
              {SUPPORTED_LOCALES.map((loc) => (
                <label key={loc} style={polje}>
                  <span style={{ ...oznaka, fontSize: 11 }}>{loc}</span>
                  <input
                    className="input"
                    value={names[loc] ?? ""}
                    onChange={(e) => setNames((prev) => ({ ...prev, [loc]: e.target.value }))}
                  />
                </label>
              ))}
            </div>
          </div>

          <div style={polje}>
            <span style={oznaka}>{t("controlPoints.form.parameters", "Šta se meri na ovoj tački")}</span>
            {groups.length === 0 ? (
              <span className="muted">
                {t("controlPoints.form.noParameters", "Katalog testova je prazan.")}
              </span>
            ) : (
              groups.map((g) => (
                <div key={g.discipline} style={{ marginTop: 8 }}>
                  <div style={{ ...oznaka, marginBottom: 6 }}>{disciplineLabel(g.discipline)}</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                    {g.items.map((p) => (
                      <label
                        key={p.id}
                        style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}
                      >
                        <input
                          type="checkbox"
                          checked={chosen.includes(p.id)}
                          onChange={() => toggle(p.id)}
                        />
                        {localizedName({ name: p.key, nameI18n: p.nameI18n }, lang)}
                        <span className="muted">{p.unit}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button type="submit" className="btn primary" disabled={busy}>
              {t("common.save", "Sačuvaj")}
            </button>
            <button type="button" className="btn" onClick={onClose} disabled={busy}>
              {t("common.cancel", "Otkaži")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
